<template>
  <div class="ocr-report-samples">
    <v-alert v-if="!filename" type="info" variant="tonal" density="compact">
      Select a report first.
    </v-alert>
    <template v-else>
      <div class="ocr-samples-head">
        <p v-if="loading" class="ocr-empty-cell">Loading samples…</p>
        <p v-else-if="!rows.length" class="ocr-empty-cell">No samples in this report.</p>
        <p v-else class="ocr-panel-lead ocr-panel-lead--flush">{{ rows.length }} sample{{ rows.length === 1 ? "" : "s" }}</p>
        <div class="ocr-toolbar-cluster">
          <v-btn size="small" variant="tonal" class="ocr-toolbar-btn" :disabled="selected.length < 2" :loading="combining" @click="combine">
            Combine
          </v-btn>
          <v-btn size="small" color="error" variant="outlined" class="ocr-toolbar-btn" :disabled="!selected.length" :loading="deleting" @click="removeSelected">
            Delete
          </v-btn>
          <v-btn size="small" color="primary" variant="flat" class="ocr-toolbar-btn" :loading="saving" @click="save">
            Save samples
          </v-btn>
        </div>
      </div>
      <v-alert
        v-if="statusMessage"
        :type="statusType"
        variant="tonal"
        density="compact"
        class="ocr-status-alert"
        closable
        @click:close="statusMessage = ''"
      >
        {{ statusMessage }}
      </v-alert>
      <OcrTableWrap v-if="!loading && rows.length" title="Samples">
        <v-table class="well-table sample-table well-table-data fd-table">
          <thead>
            <tr class="main-header">
              <th class="ocr-select-col"></th>
              <th v-for="col in columns" :key="col">{{ col }}</th>
            </tr>
          </thead>
          <tbody class="well-table-content">
            <tr
              v-for="(row, rIdx) in rows"
              :key="rIdx"
              class="ocr-table-pick-row"
              :class="{ 'ocr-table-pick-row--active': selected.includes(rIdx) }"
            >
              <td class="ocr-select-col">
                <v-checkbox
                  :model-value="selected.includes(rIdx)"
                  hide-details
                  density="compact"
                  @update:model-value="toggle(rIdx)"
                />
              </td>
              <td v-for="(col, cIdx) in columns" :key="col + rIdx">
                <input v-model="rows[rIdx][cIdx]" class="ocr-editable-grid-input" type="text" />
              </td>
            </tr>
          </tbody>
        </v-table>
      </OcrTableWrap>

      <div class="ocr-panel ocr-add-form ocr-samples-form-panel">
        <div class="ocr-samples-form-head">
          <div class="ocr-panel-title ocr-panel-title--flush">Add sample</div>
          <v-btn size="small" color="primary" variant="flat" class="ocr-toolbar-btn" :loading="adding" @click="add">
            Add sample
          </v-btn>
        </div>
        <div class="ocr-form-grid ocr-samples-form">
          <div class="ocr-field">
            <label class="profile-menu-label">Sample ID</label>
            <v-text-field v-model="newSample.fluidSampleID" density="compact" variant="outlined" hide-details />
          </div>
          <div class="ocr-field">
            <label class="profile-menu-label">Container ID</label>
            <v-text-field v-model="newSample.fluidSampleContainerID" density="compact" variant="outlined" hide-details />
          </div>
          <div class="ocr-field">
            <label class="profile-menu-label">Sample type</label>
            <v-select
              v-model="newSample.sampleType"
              :items="sampleTypes"
              density="compact"
              variant="outlined"
              hide-details
            />
          </div>
          <div class="ocr-field">
            <label class="profile-menu-label">Remark</label>
            <v-text-field v-model="newSample.remark" density="compact" variant="outlined" hide-details />
          </div>
        </div>
        <v-checkbox
          v-model="newSample.default"
          class="ocr-samples-default"
          label="Make default sample"
          hide-details
          density="compact"
        />
      </div>
    </template>
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";
import OcrTableWrap from "@/components/layout/OCR/OcrTableWrap.vue";

export default {
  name: "OcrReportSamples",
  components: { OcrTableWrap },
  props: {
    filename: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      saving: false,
      combining: false,
      deleting: false,
      adding: false,
      columns: [],
      rows: [],
      selected: [],
      sampleTypes: [""],
      statusMessage: "",
      statusType: "info",
      newSample: {
        fluidSampleID: "Manual_1",
        fluidSampleContainerID: "Unknown",
        sampleType: "",
        remark: "Added by user",
        default: false,
      },
    };
  },
  watch: {
    filename: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  methods: {
    toggle(idx) {
      const set = new Set(this.selected);
      if (set.has(idx)) set.delete(idx);
      else set.add(idx);
      this.selected = [...set];
    },
    applyPayload(data) {
      const grid = data.grid || {};
      this.columns = grid.columns || [];
      this.rows = (grid.data || []).map((row) => [...row]);
      this.sampleTypes = data.sample_types?.length ? data.sample_types : [""];
      this.newSample.fluidSampleID = data.next_manual_id || "Manual_1";
      this.selected = [];
    },
    async load() {
      if (!this.filename) {
        this.columns = [];
        this.rows = [];
        return;
      }
      this.loading = true;
      this.statusMessage = "";
      try {
        const { data } = await ocrApi.get(
          `api/v1/reports/${encodeURIComponent(this.filename)}/samples`,
        );
        this.applyPayload(data);
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Could not load samples.");
        this.statusType = "error";
      } finally {
        this.loading = false;
      }
    },
    gridBody() {
      return { grid: { columns: this.columns, data: this.rows.map((r) => [...r]) } };
    },
    async save() {
      this.saving = true;
      try {
        const { data } = await ocrApi.put(
          `api/v1/reports/${encodeURIComponent(this.filename)}/samples`,
          this.gridBody(),
        );
        this.applyPayload(data);
        this.statusMessage = "Samples saved.";
        this.statusType = "success";
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Save failed.");
        this.statusType = "error";
      } finally {
        this.saving = false;
      }
    },
    async combine() {
      this.combining = true;
      try {
        const { data } = await ocrApi.post(
          `api/v1/reports/${encodeURIComponent(this.filename)}/samples/combine`,
          { indices: this.selected },
        );
        this.applyPayload(data);
        this.statusMessage = "Samples combined.";
        this.statusType = "success";
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Combine failed.");
        this.statusType = "error";
      } finally {
        this.combining = false;
      }
    },
    async removeSelected() {
      this.deleting = true;
      try {
        const { data } = await ocrApi.post(
          `api/v1/reports/${encodeURIComponent(this.filename)}/samples/delete`,
          { indices: this.selected },
        );
        this.applyPayload(data);
        this.statusMessage = "Sample(s) deleted.";
        this.statusType = "success";
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Delete failed.");
        this.statusType = "error";
      } finally {
        this.deleting = false;
      }
    },
    async add() {
      this.adding = true;
      try {
        const { data } = await ocrApi.post(
          `api/v1/reports/${encodeURIComponent(this.filename)}/samples`,
          this.newSample,
        );
        this.applyPayload(data);
        this.newSample.fluidSampleContainerID = "Unknown";
        this.newSample.sampleType = "";
        this.newSample.remark = "Added by user";
        this.newSample.default = false;
        this.statusMessage = "Sample added.";
        this.statusType = "success";
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Add sample failed.");
        this.statusType = "error";
      } finally {
        this.adding = false;
      }
    },
  },
};
</script>
